import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Search, Edit, Trash2, Users as UsersIcon, Loader2, Key, Copy } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const roleLabels = {
  admin: "Administrador",
  admin_consultor: "Admin Consultor",
  proveedor: "Proveedor",
  cliente: "Cliente",
};

const roleColors = {
  admin: "bg-red-100 text-red-800",
  admin_consultor: "bg-purple-100 text-purple-800",
  proveedor: "bg-blue-100 text-blue-800",
  cliente: "bg-green-100 text-green-800",
};

const initialFormData = {
  email: "",
  name: "",
  password: "",
  role: "cliente",
  cliente_id: "",
  proveedor_id: "",
  proveedor_ids: [],
  email_contacto: "",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordResult, setShowPasswordResult] = useState(false); // Mostrar contraseña después de cambiarla
  const [savedPassword, setSavedPassword] = useState(""); // Contraseña guardada para mostrar
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes, suppliersRes] = await Promise.all([
        api.get("/users"),
        api.get("/clients"),
        api.get("/suppliers"),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingId(user.id);
      setFormData({
        email: user.email,
        name: user.name,
        password: "", // No mostrar contraseña actual
        role: user.role,
        cliente_id: user.cliente_id || "",
        proveedor_id: user.proveedor_id || "",
        proveedor_ids: user.proveedor_ids || (user.proveedor_id ? [user.proveedor_id] : []),
        email_contacto: user.email_contacto || "",
      });
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      toast.error("El usuario y nombre son obligatorios");
      return;
    }
    if (!editingId && !formData.password) {
      toast.error("La contraseña es obligatoria para nuevos usuarios");
      return;
    }
    if (formData.role === "proveedor" && formData.proveedor_ids.length === 0) {
      toast.error("Selecciona al menos un proveedor");
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData };
      if (editingId && !dataToSend.password) {
        delete dataToSend.password; // No enviar contraseña vacía al editar
      }
      if (!dataToSend.cliente_id) {
        dataToSend.cliente_id = null;
      }
      // Asegurar que proveedor_id sea el primero de la lista
      if (dataToSend.proveedor_ids && dataToSend.proveedor_ids.length > 0) {
        dataToSend.proveedor_id = dataToSend.proveedor_ids[0];
      }

      if (editingId) {
        await api.put(`/users/${editingId}`, dataToSend);
        toast.success("Usuario actualizado correctamente");
      } else {
        await api.post("/users", dataToSend);
        toast.success("Usuario creado correctamente");
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Usuario eliminado correctamente");
      setUsers(users.filter((u) => u.id !== deleteId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el usuario");
    } finally {
      setDeleteId(null);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await api.put(`/users/${passwordUserId}`, { password: newPassword });
      // Guardar la contraseña para mostrarla en el diálogo de resultado
      setSavedPassword(newPassword);
      setShowPasswordResult(true);
      setNewPassword("");
      fetchData();
    } catch (error) {
      toast.error("Error al cambiar la contraseña");
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordUserId(null);
    setNewPassword("");
    setShowPasswordResult(false);
    setSavedPassword("");
  };

  const copyPassword = (password) => {
    if (password) {
      navigator.clipboard.writeText(password);
      toast.success("Contraseña copiada al portapapeles");
    }
  };

  const getClientName = (clienteId) => {
    const client = clients.find((c) => c.id === clienteId);
    return client?.nombre || "-";
  };

  const getSupplierName = (proveedorId) => {
    const supplier = suppliers.find((s) => s.id === proveedorId);
    return supplier?.nombre || "-";
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión de Usuarios</h1>
          <p className="text-slate-500 mt-1">Administra usuarios, roles y permisos</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="create-user-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o usuario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-users"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <UsersIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay usuarios</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron usuarios con ese criterio" : "Crea el primer usuario"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Usuario
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Email Contacto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Rol
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Cliente/Proveedor
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Fecha Creación
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[150px]">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-mono">{user.email}</TableCell>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {user.email_contacto || <span className="text-slate-400 italic">Sin email</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={roleColors[user.role]}>
                      {roleLabels[user.role] || user.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {user.role === "cliente" ? getClientName(user.cliente_id) : 
                     user.role === "proveedor" ? (
                       user.proveedor_ids && user.proveedor_ids.length > 0 
                         ? user.proveedor_ids.map(id => getSupplierName(id)).filter(n => n !== "-").join(", ")
                         : getSupplierName(user.proveedor_id)
                     ) : "-"}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {user.created_at
                      ? format(new Date(user.created_at), "dd/MM/yyyy", { locale: es })
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenDialog(user)}
                        title="Editar"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setPasswordUserId(user.id);
                          setPasswordDialogOpen(true);
                        }}
                        title="Cambiar contraseña"
                        data-testid={`password-user-${user.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(user.id)}
                          title="Eliminar"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Usuario" : "Nuevo Usuario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Usuario *</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Nombre de usuario"
                required
                data-testid="input-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                required
                data-testid="input-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingId ? "Nueva Contraseña (dejar vacío para no cambiar)" : "Contraseña *"}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required={!editingId}
                minLength={6}
                data-testid="input-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({ 
                  ...formData, 
                  role: v,
                  // Inicializar arrays cuando se cambia de rol
                  proveedor_ids: v === "proveedor" ? (formData.proveedor_ids || []) : [],
                  cliente_id: v === "cliente" ? (formData.cliente_id || "") : "",
                })}
                disabled={editingId && formData.email === "Administrador"}
              >
                <SelectTrigger data-testid="select-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="admin_consultor">Admin Consultor (Solo lectura)</SelectItem>
                  <SelectItem value="proveedor">Proveedor</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
              {editingId && formData.email === "Administrador" && (
                <p className="text-xs text-amber-600">
                  El usuario Administrador no puede cambiar de rol
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email de Contacto</Label>
              <Input
                type="email"
                value={formData.email_contacto || ""}
                onChange={(e) => setFormData({ ...formData, email_contacto: e.target.value })}
                placeholder="correo@ejemplo.com"
                data-testid="input-user-email-contacto"
              />
              <p className="text-xs text-slate-500">
                Email para recibir notificaciones del sistema
              </p>
            </div>
            {formData.role === "cliente" && (
              <div className="space-y-2">
                <Label>Cliente Asignado</Label>
                <Select 
                  value={formData.cliente_id || "none"} 
                  onValueChange={(v) => setFormData({ ...formData, cliente_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="select-user-cliente">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  El usuario solo podrá ver los lotes de este cliente
                </p>
              </div>
            )}
            {formData.role === "proveedor" && (
              <div className="space-y-2">
                <Label>Proveedores Asignados</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-slate-50">
                  {suppliers.map((supplier) => {
                    const currentIds = Array.isArray(formData.proveedor_ids) ? formData.proveedor_ids : [];
                    const isSelected = currentIds.includes(supplier.id);
                    return (
                      <label 
                        key={supplier.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-blue-100 border border-blue-300'
                            : 'bg-white border border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            const newIds = checked
                              ? [...currentIds, supplier.id]
                              : currentIds.filter(id => id !== supplier.id);
                            setFormData(prev => ({ 
                              ...prev, 
                              proveedor_ids: newIds,
                              proveedor_id: newIds.length > 0 ? newIds[0] : ""
                            }));
                          }}
                        />
                        <span className="text-sm">{supplier.nombre}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  {(Array.isArray(formData.proveedor_ids) ? formData.proveedor_ids.length : 0) === 0 
                    ? "Selecciona al menos un proveedor"
                    : `${formData.proveedor_ids.length} proveedor(es) seleccionado(s)`}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="save-user-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingId ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={handleClosePasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {showPasswordResult ? "Contraseña Actualizada" : "Cambiar Contraseña"}
            </DialogTitle>
          </DialogHeader>
          {showPasswordResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-2">La contraseña se ha actualizado correctamente:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-green-300 px-3 py-2 rounded font-mono text-lg">
                    {savedPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyPassword(savedPassword)}
                    title="Copiar contraseña"
                    className="shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  Guarda esta contraseña en un lugar seguro antes de cerrar.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={handleClosePasswordDialog} className="bg-slate-900 hover:bg-slate-800 w-full">
                  Cerrar
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nueva Contraseña</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="input-new-password"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClosePasswordDialog}>
                  Cancelar
                </Button>
                <Button onClick={handleChangePassword} className="bg-slate-900 hover:bg-slate-800">
                  Cambiar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el usuario.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
